import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { RealtimeProvider } from "./context/RealtimeContext";
import ProtectedRoute, { defaultRouteFor } from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";

import AlumnoDashboard from "./pages/alumno/Dashboard";
import Lecciones from "./pages/alumno/Lecciones";
import LessonDetail from "./pages/alumno/LessonDetail";
import Calendario from "./pages/alumno/Calendario";
import TutorIA from "./pages/alumno/TutorIA";
import Examenes from "./pages/alumno/Examenes";
import ExamTake from "./pages/alumno/ExamTake";
import AlumnoComunicados from "./pages/alumno/Comunicados";
import Recomendaciones from "./pages/alumno/Recomendaciones";
import Perfil from "./pages/alumno/Perfil";
import LessonVersion from "./pages/alumno/LessonVersion";
import EvaluacionOralListaAlumno from "./pages/alumno/EvaluacionOralLista";
import EvaluacionOralPractica from "./pages/alumno/EvaluacionOralPractica";

import ProfesorDashboard from "./pages/profesor/Dashboard";
import MisClases from "./pages/profesor/MisClases";
import MisCursos from "./pages/profesor/MisCursos";
import CursoDetalle from "./pages/profesor/CursoDetalle";
import TareaEntregas from "./pages/profesor/TareaEntregas";
import EntregaDetalle from "./pages/profesor/EntregaDetalle";
import ReporteErroresCurso from "./pages/profesor/ReporteErroresCurso";
import Reportes from "./pages/profesor/Reportes";
import ReporteAlumno from "./pages/profesor/ReporteAlumno";
import Calificaciones from "./pages/profesor/Calificaciones";
import AsistenteIA from "./pages/profesor/AsistenteIA";
import ProfesorConsultas from "./pages/profesor/ConsultasColegio";
import ProfesorMensajeria from "./pages/profesor/Mensajeria";
import EvaluacionOralListaProfesor from "./pages/profesor/EvaluacionOralLista";
import EvaluacionOralDetalleProfesor from "./pages/profesor/EvaluacionOralDetalle";
import IncidenciasListaProfesor from "./pages/profesor/IncidenciasLista";
import CalendarioProfesor from "./pages/alumno/Calendario";

import PsicologoDashboard from "./pages/psicologo/Dashboard";
import Perfiles from "./pages/psicologo/Perfiles";
import PerfilesProfesores from "./pages/psicologo/PerfilesProfesores";
import PsicologoMensajeria from "./pages/psicologo/Mensajeria";
import PsicologoConsultas from "./pages/psicologo/ConsultasColegio";
import CalendarioPsicologo from "./pages/alumno/Calendario";

import PadreDashboard from "./pages/padre/Dashboard";
import MiHijo from "./pages/padre/MiHijo";
import PadreMensajeria from "./pages/padre/Mensajeria";
import PadreComunicados from "./pages/padre/Comunicados";
import PadreConsultas from "./pages/padre/ConsultasColegio";
import PadreIncidencias from "./pages/padre/Incidencias";
import PerfilPadre from "./pages/padre/Perfil";
import CalendarioPadre from "./pages/alumno/Calendario";

import AdminDashboard from "./pages/admin/Dashboard";
import Usuarios from "./pages/admin/Usuarios";
import AlumnosSeccionLista from "./pages/admin/AlumnosSeccionLista";
import AlumnoSeccionDetalle from "./pages/admin/AlumnoSeccionDetalle";
import TrabajadoresLista from "./pages/admin/TrabajadoresLista";
import TrabajadorDetalle from "./pages/admin/TrabajadorDetalle";
import ContratacionLista from "./pages/admin/ContratacionLista";
import ContratacionDetalle from "./pages/admin/ContratacionDetalle";
import Monedas from "./pages/admin/Monedas";
import AdminComunicados from "./pages/admin/Comunicados";
import Horarios from "./pages/admin/Horarios";
import InfoColegio from "./pages/admin/InfoColegio";
import AdminReportes from "./pages/admin/Reportes";
import IncidenciasListaAdmin from "./pages/admin/IncidenciasLista";
import CalendarioAdmin from "./pages/alumno/Calendario";

import EjecutivoDashboard from "./pages/ejecutivo/Dashboard";
import Colegios from "./pages/ejecutivo/Colegios";
import Planes from "./pages/ejecutivo/Planes";
import Estadisticas from "./pages/ejecutivo/Estadisticas";
import EjecutivoReportes from "./pages/ejecutivo/Reportes";
import GestionAdmins from "./pages/ejecutivo/Admins";
import Financiero from "./pages/ejecutivo/Financiero";
import Actividad from "./pages/ejecutivo/Actividad";
import EjecutivoRecomendaciones from "./pages/ejecutivo/Recomendaciones";

import PerfilStaff from "./pages/shared/PerfilStaff";
import EvaluacionOralReporte from "./pages/shared/EvaluacionOralReporte";
import IncidenciaAlumnoDetalle from "./pages/shared/IncidenciaAlumnoDetalle";
import Asistencia from "./pages/shared/Asistencia";
import HelpCenter from "./pages/HelpCenter";

function HomeRedirect() {
    const { user } = useAuth();
    if (user === null)
        return (
            <div className="min-h-screen flex items-center justify-center bg-edu-bg">
                <div className="text-edu-muted text-sm">Cargando…</div>
            </div>
        );
    if (user === false) return <Navigate to="/login" replace />;
    return <Navigate to={defaultRouteFor(user.role)} replace />;
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <RealtimeProvider>
                    <BrowserRouter>
                        <a href="#main-content" className="skip-link">
                            Saltar al contenido principal
                        </a>
                        <Toaster position="top-right" richColors />
                        <Routes>
                            <Route path="/" element={<HomeRedirect />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/reset-password" element={<ResetPassword />} />

                            {/* RUTAS DEL ALUMNO */}
                            <Route path="/alumno" element={<ProtectedRoute allowedRoles={["alumno"]}><Layout /></ProtectedRoute>}>
                                <Route path="dashboard" element={<AlumnoDashboard />} />
                                <Route path="lecciones" element={<Lecciones />} />
                                <Route path="lecciones/:id" element={<LessonDetail />} />
                                <Route path="lecciones/:id/aprender" element={<LessonVersion />} />
                                <Route path="calendario" element={<Calendario />} />
                                <Route path="tutor-ia" element={<TutorIA />} />
                                <Route path="examenes" element={<Examenes />} />
                                <Route path="examenes/:id" element={<ExamTake />} />
                                <Route path="comunicados" element={<AlumnoComunicados />} />
                                <Route path="recomendaciones" element={<Recomendaciones />} />
                                <Route path="evaluacion-oral" element={<EvaluacionOralListaAlumno />} />
                                <Route path="evaluacion-oral/:assessmentId" element={<EvaluacionOralPractica />} />
                                <Route path="evaluacion-oral/sesion/:sessionId" element={<EvaluacionOralReporte />} />
                                <Route path="perfil" element={<Perfil />} />
                            </Route>

                            {/* RUTAS DEL PROFESOR */}
                            <Route path="/profesor" element={<ProtectedRoute allowedRoles={["profesor"]}><Layout /></ProtectedRoute>}>
                                <Route path="dashboard" element={<ProfesorDashboard />} />
                                <Route path="mis-clases" element={<MisClases />} />
                                <Route path="mis-cursos" element={<MisCursos />} />
                                <Route path="cursos/:id" element={<CursoDetalle />} />
                                <Route path="tareas/:assignmentId" element={<TareaEntregas />} />
                                <Route path="tareas/:assignmentId/entregas/:submissionId" element={<EntregaDetalle />} />
                                <Route path="cursos/:id/reporte-errores" element={<ReporteErroresCurso />} />
                                <Route path="reportes" element={<Reportes />} />
                                <Route path="reportes/:courseId/:studentId" element={<ReporteAlumno />} />
                                <Route path="evaluacion-oral" element={<EvaluacionOralListaProfesor />} />
                                <Route path="evaluacion-oral/:assessmentId" element={<EvaluacionOralDetalleProfesor />} />
                                <Route path="evaluacion-oral/sesion/:sessionId" element={<EvaluacionOralReporte />} />
                                <Route path="incidencias" element={<IncidenciasListaProfesor />} />
                                <Route path="incidencias/:studentId" element={<IncidenciaAlumnoDetalle />} />
                                <Route path="asistencia" element={<Asistencia />} />
                                <Route path="calendario" element={<CalendarioProfesor />} />
                                <Route path="calificaciones" element={<Calificaciones />} />
                                <Route path="asistente-ia" element={<AsistenteIA />} />
                                <Route path="consultas" element={<ProfesorConsultas />} />
                                <Route path="mensajeria" element={<ProfesorMensajeria />} />
                                <Route path="perfil" element={<PerfilStaff />} />
                            </Route>

                            {/* RUTAS DEL PSICÓLOGO */}
                            <Route path="/psicologo" element={<ProtectedRoute allowedRoles={["psicologo"]}><Layout /></ProtectedRoute>}>
                                <Route path="dashboard" element={<PsicologoDashboard />} />
                                <Route path="perfiles" element={<Perfiles />} />
                                <Route path="perfiles-profesores" element={<PerfilesProfesores />} />
                                <Route path="alumnos/medico/:studentId" element={<AlumnoSeccionDetalle />} />
                                <Route path="calendario" element={<CalendarioPsicologo />} />
                                <Route path="mensajeria" element={<PsicologoMensajeria />} />
                                <Route path="consultas" element={<PsicologoConsultas />} />
                                <Route path="perfil" element={<PerfilStaff />} />
                            </Route>

                            {/* RUTAS DEL PADRE */}
                            <Route path="/padre" element={<ProtectedRoute allowedRoles={["padre"]}><Layout /></ProtectedRoute>}>
                                <Route path="dashboard" element={<PadreDashboard />} />
                                <Route path="mi-hijo" element={<MiHijo />} />
                                <Route path="calendario" element={<CalendarioPadre />} />
                                <Route path="mensajeria" element={<PadreMensajeria />} />
                                <Route path="comunicados" element={<PadreComunicados />} />
                                <Route path="consultas" element={<PadreConsultas />} />
                                <Route path="incidencias" element={<PadreIncidencias />} />
                                <Route path="perfil" element={<PerfilPadre />} />
                            </Route>

                            {/* RUTAS DEL ADMINISTRATIVO */}
                            <Route path="/admin" element={<ProtectedRoute allowedRoles={["administrativo"]}><Layout /></ProtectedRoute>}>
                                <Route path="dashboard" element={<AdminDashboard />} />
                                <Route path="usuarios" element={<Usuarios />} />
                                <Route path="alumnos/:section" element={<AlumnosSeccionLista />} />
                                <Route path="alumnos/:section/:studentId" element={<AlumnoSeccionDetalle />} />
                                <Route path="trabajadores" element={<TrabajadoresLista />} />
                                <Route path="trabajadores/:staffId" element={<TrabajadorDetalle />} />
                                <Route path="contratacion" element={<ContratacionLista />} />
                                <Route path="contratacion/:postingId" element={<ContratacionDetalle />} />
                                <Route path="monedas" element={<Monedas />} />
                                <Route path="calendario" element={<CalendarioAdmin />} />
                                <Route path="comunicados" element={<AdminComunicados />} />
                                <Route path="horarios" element={<Horarios />} />
                                <Route path="info-colegio" element={<InfoColegio />} />
                                <Route path="reportes" element={<AdminReportes />} />
                                <Route path="incidencias" element={<IncidenciasListaAdmin />} />
                                <Route path="incidencias/:studentId" element={<IncidenciaAlumnoDetalle />} />
                                <Route path="asistencia" element={<Asistencia />} />
                                <Route path="perfil" element={<PerfilStaff />} />
                            </Route>

                            {/* RUTAS DEL EJECUTIVO */}
                            <Route path="/ejecutivo" element={<ProtectedRoute allowedRoles={["ejecutivo"]}><Layout /></ProtectedRoute>}>
                                <Route path="dashboard" element={<EjecutivoDashboard />} />
                                <Route path="colegios" element={<Colegios />} />
                                <Route path="planes" element={<Planes />} />
                                <Route path="estadisticas" element={<Estadisticas />} />
                                <Route path="reportes" element={<EjecutivoReportes />} />
                                <Route path="admins" element={<GestionAdmins />} />
                                <Route path="financiero" element={<Financiero />} />
                                <Route path="actividad" element={<Actividad />} />
                                <Route path="recomendaciones" element={<EjecutivoRecomendaciones />} />
                                <Route path="perfil" element={<PerfilStaff />} />
                            </Route>

                            {/* HELP CENTER */}
                            <Route path="/help" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                                <Route index element={<HelpCenter />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </BrowserRouter>
                </RealtimeProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
